/**
 * master_employmentTypes Hook (V86)
 *
 * httpsCallable을 통해 고용형태 목록을 조회하는 React Hook입니다.
 *
 * **V86 변경:**
 * - Firestore 직접 접근 → httpsCallable 방식으로 전환
 * - 인덱스 문제 해결 (복합 인덱스 불필요)
 *
 * **용도:**
 * - 고용형태 드롭다운/선택 UI 구현
 * - 고용형태 목록 표시
 * - 고용형태 정보 조회
 *
 * **사용 예시:**
 * ```typescript
 * function EmployeeFormModal() {
 *   const { employmentTypes, loading, error } = useEmploymentTypes();
 *
 *   return (
 *     <select>
 *       {employmentTypes.map(type => (
 *         <option key={type.id} value={type.id}>
 *           {type.name} ({type.nameEn})
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 *
 * @see EMPLOYMENT_TYPE_HARDCODING_AUDIT.md
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/**
 * 고용형태 타입 (Firestore 문서 구조)
 */
export interface EmploymentType {
  /** 문서 ID (예: emp_regular, emp_contract, emp_intern, emp_dispatch) */
  id: string;

  /** 고용형태명 한글 */
  name: string;

  /** 고용형태명 영문 */
  nameEn: string;

  /** 시스템 코드 (예: REGULAR, CONTRACT, INTERN, DISPATCH) */
  code: string;

  /** 정렬 순서 */
  order: number;

  /** 사용 여부 (active/inactive) */
  status: 'active' | 'inactive';

  /** 설명 */
  description?: string;

  /** 생성일시 */
  createdAt?: any;

  /** 수정일시 */
  updatedAt?: any;

  /** 생성자 */
  createdBy?: string;

  /** 수정자 */
  updatedBy?: string;
}

/**
 * Hook 반환 타입
 */
export interface UseEmploymentTypesResult {
  /** 고용형태 목록 */
  employmentTypes: EmploymentType[];

  /** 로딩 중 여부 */
  loading: boolean;

  /** 에러 */
  error: Error | null;

  /** 재조회 함수 */
  refetch: () => Promise<void>;
}

/**
 * V86: Firestore 문서 변환은 이제 백엔드에서 처리됩니다.
 */

/**
 * master_employmentTypes 컬렉션 조회 Hook
 *
 * @param includeInactive inactive 상태 포함 여부 (기본값: false)
 * @returns UseEmploymentTypesResult
 *
 * @example
 * // active 상태만 조회
 * const { employmentTypes, loading } = useEmploymentTypes();
 *
 * // inactive 포함 조회
 * const { employmentTypes, loading } = useEmploymentTypes(true);
 */
export function useEmploymentTypes(includeInactive = false): UseEmploymentTypesResult {
  const [employmentTypes, setEmploymentTypes] = useState<EmploymentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchEmploymentTypes = async () => {
    // Firebase가 초기화되지 않은 경우 (DEMO 모드)
    if (!functions) {
      setEmploymentTypes([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // V86: httpsCallable 사용
      const getEmploymentTypesFn = httpsCallable<
        { includeInactive: boolean },
        { success: boolean; employmentTypes: EmploymentType[] }
      >(functions!, 'getEmploymentTypes');

      const result = await getEmploymentTypesFn({ includeInactive });

      if (result.data.success) {
        setEmploymentTypes(result.data.employmentTypes);
      } else {
        throw new Error('고용형태 목록 조회 실패');
      }
    } catch (err) {
      console.error('Error fetching employment types:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmploymentTypes();
  }, [includeInactive]);

  return {
    employmentTypes,
    loading,
    error,
    refetch: fetchEmploymentTypes,
  };
}

/**
 * ID로 고용형태 찾기
 *
 * @param employmentTypes 고용형태 목록
 * @param id 고용형태 ID
 * @returns EmploymentType 또는 undefined
 *
 * @example
 * const { employmentTypes } = useEmploymentTypes();
 * const regular = findEmploymentTypeById(employmentTypes, 'emp_regular');
 */
export function findEmploymentTypeById(
  employmentTypes: EmploymentType[],
  id: string
): EmploymentType | undefined {
  return employmentTypes.find(type => type.id === id);
}

/**
 * 코드로 고용형태 찾기
 *
 * @param employmentTypes 고용형태 목록
 * @param code 고용형태 코드 (예: REGULAR, CONTRACT)
 * @returns EmploymentType 또는 undefined
 *
 * @example
 * const { employmentTypes } = useEmploymentTypes();
 * const regular = findEmploymentTypeByCode(employmentTypes, 'REGULAR');
 */
export function findEmploymentTypeByCode(
  employmentTypes: EmploymentType[],
  code: string
): EmploymentType | undefined {
  return employmentTypes.find(type => type.code === code);
}
